import {ChangeDetectorRef, Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgbModal, NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';
import {firstValueFrom} from 'rxjs';
import {PostcodeDto, PostcodeService} from './postcode-service';
import {PostcodeModal} from './postcode-modal';
import {ConfirmDialogService} from '../../../shared/ui/confirm-dialog/confirm-dialog.service';
import {resolveTotalElements} from '../../../shared/commons/pagination.util';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';
import {PagesizeSelectComponent} from '../../../shared/components/pagesize-select/pagesize-select.component';
import {SearchIconComponent} from '../../../shared/components/search-icon/search-icon.component';

@Component({
  selector: 'app-postcode',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPagination, ThaiDateTimePipe, PageTitleComponent, PagesizeSelectComponent, SearchIconComponent],
  templateUrl: './postcode.html',
  styleUrl: './postcode.scss',
})
export class PostcodeComponent implements OnInit {
  private readonly service = inject(PostcodeService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(NgbModal);
  private readonly confirmDialogService = inject(ConfirmDialogService);

  protected readonly searchQuery = signal('');
  protected readonly isLoading = signal(false);
  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly collectionSize = signal(0);
  protected readonly displayedItems = signal<PostcodeDto[]>([]);

  private totalElements = 0;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.service.search(this.page() - 1, this.pageSize(), this.searchQuery()).subscribe({
      next: (data) => {
        this.displayedItems.set(data.content);
        this.totalElements = resolveTotalElements(data);
        this.collectionSize.set(this.totalElements);
        this.isLoading.set(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading.set(false);
        this.cdr.detectChanges();
      }
    });
  }

  onSearchChange(): void {
    this.page.set(1);
    this.load();
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.onSearchChange();
  }

  openAddModal(): void {
    const modalRef = this.modalService.open(PostcodeModal, {size: 'lg'});
    modalRef.componentInstance.onSave = async (result: PostcodeDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed) {
        return false;
      }
      try {
        await firstValueFrom(this.service.createPostcode(result));
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openEditModal(item: PostcodeDto): void {
    const modalRef = this.modalService.open(PostcodeModal, {size: 'lg'});
    modalRef.componentInstance.postcode = item;
    modalRef.componentInstance.onSave = async (result: PostcodeDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed || result.id == null) {
        return false;
      }
      try {
        await firstValueFrom(this.service.updatePostcode(result.id, result));
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openViewModal(item: PostcodeDto): void {
    const modalRef = this.modalService.open(PostcodeModal, {size: 'lg'});
    modalRef.componentInstance.postcode = item;
    modalRef.componentInstance.viewOnly = true;
  }

  async deletePostcode(id: number): Promise<void> {
    const confirmed = await this.confirmDialogService.confirmDelete('คุณต้องการลบข้อมูลรหัสไปรษณีย์นี้ใช่หรือไม่?');
    if (!confirmed) {
      return;
    }
    this.service.deletePostcode(id).subscribe(() => this.load());
  }
}
